import type { EncaisseApiType } from '@/types';
import { formatMonetaire } from '@/utils';
import TileBase from './TileBase';
import TileHeader from './TileHeader';

const SEUIL_WARNING = 80; // Passe en orange à 80%

type EncaisseProgressPropsType = {
  label: string;
  encaisse: number;
  capaciteRestante: number;
  sansPlafond?: boolean;
};

const EncaisseProgress = ({
  label,
  encaisse,
  capaciteRestante,
  sansPlafond = false,
}: EncaisseProgressPropsType) => {
  const total = encaisse + capaciteRestante;
  const percent = total > 0 ? (encaisse / total) * 100 : 0;
  const isWarning = percent >= SEUIL_WARNING;

  if (sansPlafond) {
    return (
      <div className="encaisse-progress">
        <div className="encaisse-progress-header">
          <span className="encaisse-progress-label">{label}</span>
          <span className="encaisse-progress-value">{formatMonetaire(encaisse, 2, true)}</span>
        </div>
        <div className="encaisse-progress-capacity">{`${label} non plafonnée`}</div>
      </div>
    );
  }

  return (
    <div className="encaisse-progress">
      <div className="encaisse-progress-header">
        <span className="encaisse-progress-label">{label}</span>
        <span className="encaisse-progress-value">
          {`${formatMonetaire(encaisse, 2, true)} / ${formatMonetaire(total, 2, true)}`}
        </span>
      </div>
      <div className={`encaisse-progress-bar ${isWarning ? 'warning' : ''}`}>
        <div className="encaisse-progress-bar-fill" style={{ width: `${Math.min(100, percent)}%` }}>
          <span className="encaisse-progress-bar-percent">{`${percent.toFixed(2)} %`}</span>
        </div>
      </div>
      <div className="encaisse-progress-capacity">
        {`Capacité d'encaissement restante : ${formatMonetaire(capaciteRestante, 2, true)}`}
      </div>
    </div>
  );
};

type TileEncaissePropsType = {
  encaisse: EncaisseApiType | null;
  loading?: boolean;
};

const TileEncaisse = ({ encaisse, loading }: TileEncaissePropsType) => (
  <TileBase className="tile-encaisse" loading={loading}>
    <TileHeader title="Encaisse" />

    <div className="tile-encaisse-items">
      <EncaisseProgress
        label="Encaisse globale de la régie"
        encaisse={encaisse?.encaisseGlobale || 0}
        capaciteRestante={encaisse?.capaciteEncaissementGlobaleRestante || 0}
      />
      <EncaisseProgress
        label="Encaisse numéraire"
        encaisse={encaisse?.encaisseNumeraire || 0}
        capaciteRestante={encaisse?.capaciteEncaissementNumeraireRestante || 0}
        // Le plafond numéraire est facultatif : l'API le renvoie à 0, d'où encaisse + capacité <= 0
        sansPlafond={
          !!encaisse &&
          encaisse.encaisseNumeraire + encaisse.capaciteEncaissementNumeraireRestante <= 0
        }
      />
    </div>
  </TileBase>
);

export default TileEncaisse;
